import { useMemo } from "react";
import type {
  ApiGameMessage,
  ApiScoringEvent,
} from "../../../../../api/index.ts";
import { useCurrentGamePicks } from "../../../hooks/use-current-game-picks.ts";
import { useCurrentMessages } from "../../../hooks/use-current-messages.ts";
import { useCurrentScoringAndRank } from "../../../hooks/use-current-scoring-and-rank.ts";
import { getEventPoints } from "../logic/get-event-points.ts";

type ScoringEvent = {
  kind: "SCORING_EVENT";
  points: {
    base: number;
    powerup: number;
    total: number;
  };
  visibleAfter: string;
} & ApiScoringEvent;

type NonFeaturedMessage = {
  kind: "MESSAGE";
} & ApiGameMessage;

type FeaturedMessage = {
  kind: "FEATURED_MESSAGE";
} & ApiGameMessage;

export type LiveFeedEntry = ScoringEvent | NonFeaturedMessage | FeaturedMessage;

export function useLiveFeedEntries() {
  const messagesQuery = useCurrentMessages();
  const scoringQuery = useCurrentScoringAndRank();
  const picksQuery = useCurrentGamePicks();

  const pickedPlayerIds = useMemo(() => {
    return new Set(picksQuery.data?.map((pick) => pick.playerId) || []);
  }, [picksQuery.data]);

  const entries: LiveFeedEntry[] = useMemo(() => {
    const events = scoringQuery.data?.events
      ?.filter((event) => pickedPlayerIds.has(event.playerId))
      ?.map((event) => ({
        ...event,
        kind: "SCORING_EVENT" as const,
        points: getEventPoints(event, picksQuery.data || []),
        // Add the `visibleAfter` property for sorting with messages
        visibleAfter: event.createdAt,
      }));

    const messages = messagesQuery.data
      ?.map((message) => ({
        ...message,
        kind: message.featured
          ? ("FEATURED_MESSAGE" as const)
          : ("MESSAGE" as const),
      }))
      // Messages should only be visible after their visibleAfter timestamp
      ?.filter((message) => {
        const visibleAfter = new Date(message.visibleAfter);
        const now = new Date();

        return visibleAfter <= now;
      });

    return [...(events || []), ...(messages || [])].sort((a, b) =>
      compareByVisibleAfter(a, b),
    );
  }, [messagesQuery.data, scoringQuery.data, picksQuery.data, pickedPlayerIds]);

  return {
    entries,
    isLoading:
      messagesQuery.isLoading || scoringQuery.isLoading || picksQuery.isLoading,
    isError:
      messagesQuery.isError || scoringQuery.isError || picksQuery.isError,
  };
}

function compareByVisibleAfter(
  a: { visibleAfter: string },
  b: { visibleAfter: string },
) {
  return (
    new Date(b.visibleAfter).getTime() - new Date(a.visibleAfter).getTime()
  );
}
